import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Drawer, Form, Input, Popconfirm, DatePicker, Skeleton, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FlagOutlined, ReloadOutlined } from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { publicHolidayService, PublicHoliday } from '../services/publicHolidayService';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PageHeader, SectionCard, EmptyState, FilterChips, Pill } from '../components/ui';
import './PublicHolidays.css';

const PublicHolidays: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [editingHoliday, setEditingHoliday] = useState<PublicHoliday | null>(null);
  const [year, setYear] = useState(dayjs().year());

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      // The API returns every year at once, so the year switch is a filter, not a request
      setHolidays(await publicHolidayService.getAllHolidays());
    } catch (error) {
      toast.error(t('publicHolidays.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const openForm = (holiday?: PublicHoliday) => {
    setEditingHoliday(holiday ?? null);
    if (holiday) form.setFieldsValue({ ...holiday, date: dayjs(holiday.date) });
    else form.resetFields();
    setDrawerVisible(true);
  };

  const handleDelete = async (holiday: PublicHoliday) => {
    try {
      await publicHolidayService.deleteHoliday(holiday.id);
      toast.success(t('publicHolidays.deleteSuccess'));
      fetchHolidays();
    } catch (error) {
      toast.error(t('publicHolidays.deleteError'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSaving(true);
      const date = values.date.format('YYYY-MM-DD');
      if (editingHoliday) {
        await publicHolidayService.updateHoliday(editingHoliday.id, { name: values.name, date });
        toast.success(t('publicHolidays.updateSuccess'));
      } else {
        await publicHolidayService.createHoliday(values.name, date);
        toast.success(t('publicHolidays.createSuccess'));
      }
      setDrawerVisible(false);
      setYear(values.date.year()); // show the year the holiday belongs to
      fetchHolidays();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        Object.values(error.response.data.errors as Record<string, string[]>).forEach((messages) =>
          toast.error(messages[0]),
        );
      } else {
        toast.error(error.response?.data?.message || t('publicHolidays.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------- Grouping ----------
  const years = useMemo(() => {
    const set = new Set<number>(holidays.map((holiday) => dayjs(holiday.date).year()));
    set.add(dayjs().year());
    return Array.from(set).sort((a, b) => a - b);
  }, [holidays]);

  const ofYear = useMemo(
    () =>
      holidays
        .filter((holiday) => dayjs(holiday.date).year() === year)
        .sort((a, b) => a.date.localeCompare(b.date)),
    [holidays, year],
  );

  const months = useMemo(() => {
    const map = new Map<number, PublicHoliday[]>();
    ofYear.forEach((holiday) => {
      const month = dayjs(holiday.date).month();
      map.set(month, [...(map.get(month) ?? []), holiday]);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [ofYear]);

  const today = dayjs().startOf('day');
  const nextHoliday = useMemo(
    () =>
      [...holidays]
        .filter((holiday) => !dayjs(holiday.date).isBefore(today, 'day'))
        .sort((a, b) => a.date.localeCompare(b.date))[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [holidays],
  );
  const daysUntilNext = nextHoliday ? dayjs(nextHoliday.date).startOf('day').diff(today, 'day') : 0;

  const monthName = (month: number) =>
    new Date(year, month, 1).toLocaleDateString(i18n.language, { month: 'long' });

  return (
    <MainLayout bare>
      <PageHeader
        title={t('publicHolidays.title')}
        subtitle={loading ? undefined : t('publicHolidays.countInYear', { count: ofYear.length, year })}
        actions={
          <>
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchHolidays} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
              {t('publicHolidays.newHoliday')}
            </Button>
          </>
        }
      />

      {/* Next holiday */}
      {!loading && nextHoliday && (
        <div className="e22-card hol-next">
          <div className="hol-next-date">
            <b>{dayjs(nextHoliday.date).format('D')}</b>
            <small>{dayjs(nextHoliday.date).toDate().toLocaleDateString(i18n.language, { month: 'short' })}</small>
          </div>
          <div className="e22-row-body">
            <div className="hol-next-label">{t('publicHolidays.nextHoliday')}</div>
            <div className="hol-next-name">{nextHoliday.name}</div>
            <div className="hol-next-sub">
              {dayjs(nextHoliday.date).toDate().toLocaleDateString(i18n.language, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>
          </div>
          <div className="hol-next-countdown">
            {daysUntilNext === 0 ? t('publicHolidays.today') : t('publicHolidays.inDays', { count: daysUntilNext })}
          </div>
        </div>
      )}

      <SectionCard
        title={String(year)}
        icon={<FlagOutlined />}
        subtitle={loading ? undefined : t('publicHolidays.countInYear', { count: ofYear.length, year })}
        action={
          <FilterChips<number>
            ariaLabel={t('publicHolidays.title')}
            value={year}
            onChange={setYear}
            options={years.map((value) => ({
              key: value,
              label: String(value),
              count: holidays.filter((holiday) => dayjs(holiday.date).year() === value).length,
            }))}
          />
        }
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : ofYear.length === 0 ? (
          <EmptyState
            icon={<FlagOutlined />}
            text={t('publicHolidays.noHolidays', { year })}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm()}>
                {t('publicHolidays.newHoliday')}
              </Button>
            }
          />
        ) : (
          months.map(([month, list]) => (
            <section key={month} className="hol-month">
              <div className="hol-month-name">{monthName(month)}</div>
              {list.map((holiday) => {
                const date = dayjs(holiday.date);
                const past = date.isBefore(today, 'day');
                const weekend = date.day() === 0 || date.day() === 6;
                return (
                  <div className={`e22-row hol-row ${past ? 'hol-row--past' : ''}`} key={holiday.id}>
                    <div className="hol-date">
                      <b>{date.format('D')}</b>
                      <small>{date.toDate().toLocaleDateString(i18n.language, { weekday: 'short' })}</small>
                    </div>
                    <div className="e22-row-body">
                      <div className="e22-row-title">{holiday.name}</div>
                      <div className="e22-row-subtitle">
                        {date.toDate().toLocaleDateString(i18n.language, {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                    {date.isSame(today, 'day') ? <Pill tone="success">{t('publicHolidays.today')}</Pill> : null}
                    {weekend ? (
                      <Tooltip title={t('publicHolidays.weekendHint')}>
                        <span>
                          <Pill tone="violet">{t('attendance.weekend')}</Pill>
                        </span>
                      </Tooltip>
                    ) : null}
                    <Tooltip title={t('publicHolidays.editHoliday')}>
                      <Button type="text" icon={<EditOutlined />} onClick={() => openForm(holiday)} />
                    </Tooltip>
                    <Popconfirm
                      title={t('publicHolidays.deleteConfirm')}
                      description={holiday.name}
                      onConfirm={() => handleDelete(holiday)}
                      okText={t('common.yes')}
                      cancelText={t('common.no')}
                      okButtonProps={{ danger: true }}
                    >
                      <Tooltip title={t('common.delete')}>
                        <Button type="text" danger icon={<DeleteOutlined />} />
                      </Tooltip>
                    </Popconfirm>
                  </div>
                );
              })}
            </section>
          ))
        )}
      </SectionCard>

      <Drawer
        title={editingHoliday ? t('publicHolidays.editHoliday') : t('publicHolidays.newHoliday')}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={460}
        footer={
          <div className="hol-drawer-footer">
            <Button onClick={() => setDrawerVisible(false)}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editingHoliday ? t('common.update') : t('common.create')}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('publicHolidays.name')}
            rules={[{ required: true, message: t('publicHolidays.nameRequired') }]}
          >
            <Input autoFocus placeholder={t('publicHolidays.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="date"
            label={t('publicHolidays.date')}
            rules={[{ required: true, message: t('publicHolidays.dateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <div className="hol-hint">{t('publicHolidays.weekendHint')}</div>
        </Form>
      </Drawer>
    </MainLayout>
  );
};

export default PublicHolidays;
